import * as React from "react";
import Alert from "react-bootstrap/Alert";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import { Helmet } from "react-helmet-async";
import Image from "react-bootstrap/Image";
import Row from "react-bootstrap/Row";
import sortBy from "lodash/sortBy";
import { useParams } from "react-router-dom";

import { AppEvent } from "../types";
import { PageSpinner } from "../util";

import "../styles/photos.css";

type PhotosResponse = Record<
  string,
  AppEvent & {
    photo_thumbnail_links: Array<string>;
    photo_more_link: string;
  }
>;

const Photos = () => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [photos, setPhotos] = React.useState<PhotosResponse>({});
  const { accessToken } = useParams();

  React.useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        const response = await window.fetch(`/api/photos/${accessToken!}`);
        if (response.status === 200) {
          setPhotos(
            ((await response.json()) as { photos: PhotosResponse }).photos
          );
        } else {
          setError(((await response.json()) as { error: string }).error);
        }
      } catch (error) {
        setError(String(error));
      } finally {
        setLoading(false);
      }
    })();
  }, [accessToken]);

  if (loading) {
    return <PageSpinner />;
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Photos</title>
      </Helmet>
      <Container className="d-flex flex-column gap-4 text-light photos-container">
        {Object.entries(sortBy(photos, "start_date")).map(
          ([
            eventSlug,
            {
              name,
              photo_more_link: photoMoreLink,
              photo_thumbnail_links: photoThumbnailLinks,
            },
          ]) => (
            <section key={eventSlug}>
              <Row>
                <Col
                  className="d-flex flex-column justify-content-center align-items-center title-container"
                  md={2}
                >
                  <h1>{name}</h1>
                  <h6>
                    <a
                      className="text-decoration-none link-secondary"
                      href={photoMoreLink}
                      rel="noreferrer"
                      target="_blank"
                    >
                      View all
                    </a>
                  </h6>
                </Col>
                <Col
                  className="d-flex gap-3 justify-content-between images-container"
                  md={10}
                >
                  {photoThumbnailLinks.map((link, i) => (
                    <Image
                      key={i}
                      rounded
                      src={link}
                      style={{
                        height: "14.5rem",
                        width: "auto",
                        maxWidth: "unset",
                      }}
                    />
                  ))}
                </Col>
              </Row>
            </section>
          )
        )}
      </Container>
    </>
  );
};

export default Photos;
